import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";

const UNITS = ["Nos", "Kg", "Ltr", "Mtr", "Set", "Pair", "Box", "Pack"];
const GST_OPTIONS = [0, 5, 12, 18, 28];
const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Lightweight in-process cache to avoid re-firing the same search across rows
const _suggestionCache = new Map();

function ItemRow({ item, index, isViewMode, canRemove, onItemChange, onRemove, showNatureOfItem, getAuthHeader }) {
  const itemTotal = (parseFloat(item.estimated_cost) || 0) * (parseInt(item.quantity) || 1);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  // Click-outside to close suggestions panel
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setShowSuggestions(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 2) { setSuggestions([]); return; }
    const key = q.toLowerCase();
    if (_suggestionCache.has(key)) { setSuggestions(_suggestionCache.get(key)); setShowSuggestions(true); return; }
    axios.get(API_URL + "/item-master/search?q=" + encodeURIComponent(q) + "&limit=8", getAuthHeader ? getAuthHeader() : {})
      .then(r => { _suggestionCache.set(key, r.data || []); setSuggestions(r.data || []); setShowSuggestions(true); })
      .catch(() => setSuggestions([]));
  };

  const onDescriptionChange = (val) => {
    onItemChange(index, "description", val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(val), 250);
    setShowSuggestions(true);
  };

  const applySuggestion = (s) => {
    onItemChange(index, "description", s.description);
    if (s.hsn_code) onItemChange(index, "hsn_code", s.hsn_code);
    if (s.gst_percentage != null) onItemChange(index, "gst_percentage", s.gst_percentage);
    if (s.unit) onItemChange(index, "unit", s.unit);
    if (s.estimated_cost && !item.estimated_cost) onItemChange(index, "estimated_cost", s.estimated_cost);
    setShowSuggestions(false);
  };

  return React.createElement("div", { className: "p-4 border border-slate-200 rounded-lg bg-slate-50", ref: containerRef },
    React.createElement("div", { className: "flex items-center justify-between mb-4" },
      React.createElement("span", { className: "text-sm font-semibold text-slate-700 bg-slate-200 px-2 py-1 rounded" }, "Item #" + item.serial_no),
      !isViewMode && canRemove && React.createElement(Button, {
        type: "button", variant: "ghost", size: "sm", onClick: onRemove,
        className: "text-rose-600 hover:text-rose-700 hover:bg-rose-50",
        "data-testid": "remove-item-" + index
      }, "\u2715 Remove")
    ),
    // Item Description with type-ahead suggestions
    React.createElement("div", { className: "mb-4 relative" },
      React.createElement(Label, null, "Item Description *"),
      React.createElement(Textarea, {
        value: item.description,
        onChange: (e) => onDescriptionChange(e.target.value),
        onFocus: () => { if ((item.description || "").length >= 2) { fetchSuggestions(item.description); setShowSuggestions(true); } },
        className: "mt-1.5 min-h-20",
        disabled: isViewMode,
        placeholder: "Start typing\u2026 we will suggest items procured earlier",
        "data-testid": "item-description-" + index
      }),
      !isViewMode && showSuggestions && suggestions.length > 0 && React.createElement("div", {
        className: "absolute left-0 right-0 top-full mt-1 z-30 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg",
        "data-testid": "item-suggestions-" + index
      },
        suggestions.map((s, i) => React.createElement("button", {
          key: i, type: "button",
          className: "w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 flex items-start justify-between gap-3",
          onClick: () => applySuggestion(s),
          "data-testid": "item-suggestion-" + index + "-" + i
        },
          React.createElement("div", { className: "flex-1 min-w-0" },
            React.createElement("div", { className: "text-sm font-medium text-slate-900 truncate" }, s.description),
            React.createElement("div", { className: "text-xs text-slate-500" },
              [s.hsn_code && ("HSN " + s.hsn_code), s.gst_percentage != null && ("GST " + s.gst_percentage + "%"), s.unit, s.estimated_cost && ("\u20B9" + Number(s.estimated_cost).toLocaleString("en-IN"))].filter(Boolean).join("  \u2022  ")
            )
          ),
          React.createElement("span", { className: "shrink-0 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded " + (s.source === "MASTER" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500") }, s.source === "MASTER" ? "Master" : "History")
        ))
      )
    ),
    // HSN, GST, Quantity, Unit, Cost, Total row
    React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-6 gap-4 mb-4" },
      React.createElement("div", null,
        React.createElement(Label, null, "HSN Code"),
        React.createElement(Input, {
          value: item.hsn_code || "",
          onChange: (e) => onItemChange(index, "hsn_code", e.target.value),
          className: "mt-1.5", disabled: isViewMode, placeholder: "e.g. 8471",
          "data-testid": "item-hsn-" + index
        })
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "GST %"),
        React.createElement("select", {
          value: item.gst_percentage != null && item.gst_percentage !== "" ? String(item.gst_percentage) : "",
          onChange: (e) => onItemChange(index, "gst_percentage", e.target.value === "" ? null : parseFloat(e.target.value)),
          className: "w-full mt-1.5 p-2 border rounded-md",
          disabled: isViewMode,
          "data-testid": "item-gst-" + index
        },
          React.createElement("option", { value: "" }, "\u2014"),
          ...GST_OPTIONS.map(g => React.createElement("option", { key: g, value: g }, g + "%"))
        )
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "Quantity *"),
        React.createElement(Input, {
          type: "number", value: item.quantity,
          onChange: (e) => onItemChange(index, "quantity", e.target.value),
          className: "mt-1.5", min: "1", disabled: isViewMode,
          "data-testid": "item-quantity-" + index
        })
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "Unit"),
        React.createElement("select", {
          value: item.unit, onChange: (e) => onItemChange(index, "unit", e.target.value),
          className: "w-full mt-1.5 p-2 border rounded-md", disabled: isViewMode,
          "data-testid": "item-unit-" + index
        }, UNITS.map(u => React.createElement("option", { key: u, value: u }, u)))
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "Unit Cost (\u20B9) *"),
        React.createElement(Input, {
          type: "number", value: item.estimated_cost,
          onChange: (e) => onItemChange(index, "estimated_cost", e.target.value),
          className: "mt-1.5", min: "0", disabled: isViewMode, placeholder: "Cost per unit",
          "data-testid": "item-cost-" + index
        })
      ),
      React.createElement("div", null,
        React.createElement(Label, null, "Total (\u20B9)"),
        React.createElement("div", { className: "mt-1.5 p-2 bg-white border rounded-md font-semibold text-slate-900" }, "\u20B9" + itemTotal.toLocaleString("en-IN"))
      )
    ),
    // Nature of Item - Per line item (only shown in Tender mode)
    showNatureOfItem && React.createElement("div", { className: "p-3 bg-white border rounded-lg" },
      React.createElement(Label, { className: "text-sm font-medium mb-2 block" }, "Nature of Item"),
      React.createElement("div", { className: "flex flex-wrap gap-4" },
        React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
          React.createElement("input", {
            type: "radio", name: "nature_of_item_" + index,
            checked: item.nature_of_item === "CLASS_I_LOCAL",
            onChange: () => onItemChange(index, "nature_of_item", "CLASS_I_LOCAL"),
            disabled: isViewMode, "data-testid": "item-nature-class1-" + index
          }),
          React.createElement("span", { className: "text-sm" },
            React.createElement("span", { className: "font-medium" }, "Class I Local"),
            React.createElement("span", { className: "text-slate-500 ml-1" }, "(content > 50%)")
          )
        ),
        React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
          React.createElement("input", {
            type: "radio", name: "nature_of_item_" + index,
            checked: item.nature_of_item === "CLASS_II_LOCAL",
            onChange: () => onItemChange(index, "nature_of_item", "CLASS_II_LOCAL"),
            disabled: isViewMode, "data-testid": "item-nature-class2-" + index
          }),
          React.createElement("span", { className: "text-sm" },
            React.createElement("span", { className: "font-medium" }, "Class II Local"),
            React.createElement("span", { className: "text-slate-500 ml-1" }, "(content 20-50%)")
          )
        ),
        React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
          React.createElement("input", {
            type: "radio", name: "nature_of_item_" + index,
            checked: item.nature_of_item === "IMPORT",
            onChange: () => onItemChange(index, "nature_of_item", "IMPORT"),
            disabled: isViewMode, "data-testid": "item-nature-import-" + index
          }),
          React.createElement("span", { className: "text-sm" },
            React.createElement("span", { className: "font-medium" }, "Import"),
            React.createElement("span", { className: "text-slate-500 ml-1" }, "(content < 20%)")
          )
        )
      )
    )
  );
}

export default ItemRow;
